import * as React from "react";
import { usePortalSeo } from "../config";
import { Link, Outlet, useMatches } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Container,
  PageHeader,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import { AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import {
  formatAud,
  formatDateTime,
  ReconcileSignIn,
  RECON_STATUS_BADGE,
  RECON_STATUS_LABEL,
} from "../components/ReconcilePayments";

/**
 * /reconcile — the account manager's payment worklist. Lives OUTSIDE the
 * Cloudflare Access-gated /admin area: access is a magic-link session whose
 * email is on ACCOUNT_MANAGER_EMAILS (or ADMIN_EMAILS), enforced by the API.
 * Emailed reconcile links land on /reconcile/$ref already signed in.
 */

const api = apiClient("");

export function ReconcileLayout() {
  usePortalSeo("Reconciliation");
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["auth", "me"], queryFn: () => api.authMe(), retry: false });
  const email = me.data?.email ?? null;
  // When a child route ($ref) is active it renders through <Outlet />.
  const matches = useMatches();
  const hasChild = matches.some((m) => m.routeId === "/reconcile/$ref");

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Reconciliation"
        description="Record payments as they land — partial or full — and send the admin a summary when you're done."
      />
      <Section tone="paper" spacing="lg">
        <Container>
          {me.isLoading ? (
            <p className="text-sm text-muted">Checking your session…</p>
          ) : email === null ? (
            <ReconcileSignIn
              requestLink={(input) => api.requestLink(input)}
              redirect="/reconcile"
              onSignedIn={() => void qc.invalidateQueries({ queryKey: ["auth", "me"] })}
            />
          ) : hasChild ? (
            <Outlet />
          ) : (
            <ReconcileList email={email} />
          )}
        </Container>
      </Section>
    </>
  );
}

/** "Done" button — emails the admin everything recorded since the last summary. */
export function SummaryDoneButton() {
  const summary = useMutation({ mutationFn: () => api.reconcileSummary() });
  return (
    <div className="flex items-center gap-3">
      <Button
        type="button"
        variant="accent"
        size="sm"
        disabled={summary.isPending}
        onClick={() => summary.mutate()}
      >
        {summary.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Sending…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" aria-hidden="true" /> Done — email summary to admin
          </>
        )}
      </Button>
      {summary.isSuccess && (
        <span className="text-sm text-muted" role="status">
          {summary.data.events === 0
            ? "Nothing new since the last summary."
            : `Summary of ${summary.data.events} update${summary.data.events === 1 ? "" : "s"} ${
                summary.data.emailed ? "emailed to the admin." : "recorded (email failed — try again)."
              }`}
        </span>
      )}
      {summary.isError && (
        <span className="text-sm text-accent" role="alert">
          Couldn&apos;t send the summary — please try again.
        </span>
      )}
    </div>
  );
}

function ReconcileList({ email }: { email: string }) {
  const qc = useQueryClient();
  const [showPaid, setShowPaid] = React.useState(false);

  const list = useQuery({
    queryKey: ["reconcile", "enrolments", showPaid ? "paid" : "attention"],
    queryFn: () => api.reconcileList(showPaid ? { payment_status: "paid" } : {}),
    retry: (count, err) =>
      !(err instanceof ApiError && (err.status === 401 || err.status === 403)) && count < 2,
  });

  const notAuthorised =
    list.error instanceof ApiError && (list.error.status === 401 || list.error.status === 403);

  async function signOut() {
    await api.signOut().catch(() => {});
    await qc.invalidateQueries({ queryKey: ["auth", "me"] });
  }

  if (notAuthorised) {
    return (
      <Card>
        <CardContent className="flex items-start gap-3 p-6 text-sm">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
          <div>
            <p className="font-semibold text-ink">
              {email} isn&apos;t authorised for reconciliation
            </p>
            <p className="mt-1 text-muted">
              Only the account manager and admin emails can use this area. Sign out and use
              the email your reconcile links are sent to.
            </p>
            <Button type="button" size="sm" variant="outline" className="mt-3" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const rows = list.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          Signed in as <strong className="text-ink">{email}</strong>{" "}
          <button
            type="button"
            className="ml-2 text-xs font-semibold text-muted underline underline-offset-2 hover:text-ink"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </p>
        <SummaryDoneButton />
      </div>

      <div role="tablist" aria-label="Filter" className="flex gap-2">
        <Button
          type="button"
          role="tab"
          aria-selected={!showPaid}
          size="sm"
          variant={showPaid ? "outline" : "default"}
          onClick={() => setShowPaid(false)}
        >
          To reconcile
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={showPaid}
          size="sm"
          variant={showPaid ? "default" : "outline"}
          onClick={() => setShowPaid(true)}
        >
          Paid
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{showPaid ? "Paid" : "Awaiting reconciliation"}</CardTitle>
          <CardDescription>
            {list.isLoading
              ? "Loading…"
              : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {list.isLoading ? (
            <div className="flex items-center gap-2 py-10 text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <CheckCircle2 className="h-8 w-8 text-success" aria-hidden="true" />
              <p className="font-semibold text-ink">
                {showPaid ? "No paid records yet" : "Nothing to reconcile"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Parent / child</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Flagged sent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono font-semibold">
                      <Link
                        to="/reconcile/$ref"
                        params={{ ref: e.payment_ref }}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {e.payment_ref}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-ink">{e.parent_name}</div>
                      <div className="text-xs text-muted">
                        {e.child_name} · {e.year_level}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={RECON_STATUS_BADGE[e.payment_status]}>
                        {RECON_STATUS_LABEL[e.payment_status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-ink">
                      {formatAud(e.amount_cents)}
                    </TableCell>
                    <TableCell className="text-right text-ink">
                      {formatAud(e.received_cents)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-ink">
                      {formatAud(Math.max(e.balance_cents, 0))}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted">
                      {formatDateTime(e.intent_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
